/**
 * ConnectionGuideModal — "Connect your agent" modal.
 *
 * Lets the user pick a client (Claude Code / Claude Desktop / Codex / Cursor),
 * generate a token with a pre-filled name, and follow per-client setup steps
 * with copy-able command/URL blocks.
 *
 * Token list lives on the Settings page — NOT inside this modal.
 */

import { useState, useEffect } from 'react';
import { Modal } from '../Modal/Modal';
import { useCreateApiToken } from '../../hooks/useApiTokens';
import { buildMcpUrl } from '../../utils/mcpUrl';
import type { ApiTokenCreated } from '../../types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ClientId = 'claude-code' | 'claude-desktop' | 'codex' | 'cursor';

interface ClientConfig {
  id: ClientId;
  label: string;
  tokenName: string;
  icon: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function safeCopy(text: string): void {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => {
      // clipboard write failed — ignore silently since it's a non-critical UX action
    });
  }
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function TerminalIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 5h16v14H4z" />
      <path d="M8 9l3 3-3 3M13 15h3" />
    </svg>
  );
}

function DesktopIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="14" rx="2" />
      <path d="M8 21h8M12 18v3" />
    </svg>
  );
}

function CodexIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 5h16v14H4z" />
      <path d="M7 9l3 3-3 3" />
    </svg>
  );
}

function CursorIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 3l14 8-6 2-2 6z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Client configs
// ---------------------------------------------------------------------------

const CLIENTS: ClientConfig[] = [
  { id: 'claude-code', label: 'Claude Code', tokenName: 'Claude Code MCP', icon: <TerminalIcon /> },
  { id: 'claude-desktop', label: 'Claude Desktop', tokenName: 'Claude Desktop MCP', icon: <DesktopIcon /> },
  { id: 'codex', label: 'Codex', tokenName: 'Codex MCP', icon: <CodexIcon /> },
  { id: 'cursor', label: 'Cursor', tokenName: 'Cursor MCP', icon: <CursorIcon /> },
];

// ---------------------------------------------------------------------------
// CodeBlock — code block with a copy button
// ---------------------------------------------------------------------------

interface CodeBlockProps {
  code: string;
  copyText: string;
  disabled?: boolean;
  disabledTitle?: string;
}

function CodeBlock({ code, copyText, disabled = false, disabledTitle }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    if (disabled) return;
    safeCopy(copyText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex items-stretch border border-gray-700 rounded-lg overflow-hidden bg-gray-900/70">
      <pre className="flex-1 m-0 px-3 py-2.5 text-xs text-gray-200 font-mono whitespace-pre-wrap break-all overflow-auto leading-relaxed">
        {code}
      </pre>
      <button
        type="button"
        onClick={handleCopy}
        disabled={disabled}
        title={disabled ? disabledTitle : undefined}
        aria-label="Copy"
        className={`shrink-0 px-4 flex items-center gap-1.5 text-xs font-bold border-l border-gray-700 transition-colors whitespace-nowrap ${
          disabled
            ? 'bg-gray-800/70 text-gray-600 cursor-not-allowed'
            : copied
              ? 'bg-primary-900/40 text-primary-400'
              : 'bg-primary-600 hover:bg-primary-500 text-white cursor-pointer'
        }`}
      >
        {copied ? (
          <>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Copied
          </>
        ) : (
          <>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="9" width="11" height="11" rx="2" />
              <path d="M5 15V5a2 2 0 012-2h10" />
            </svg>
            Copy
          </>
        )}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-client step content
// ---------------------------------------------------------------------------

interface StepContentProps {
  clientId: ClientId;
  token: string; // '<your-token>' when not generated yet, full token after
  copyEnabled: boolean;
}

function StepContent({ clientId, token, copyEnabled }: StepContentProps) {
  const mcpUrl = buildMcpUrl(token);
  const disabledTitle = 'Generate a token above to copy a working URL.';

  if (clientId === 'claude-code') {
    const command = `claude mcp add --transport http sharpelab ${mcpUrl}`;
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-300">Step 1 — Add the MCP server</p>
          <p className="text-xs text-gray-400">Run this command in your terminal:</p>
          <CodeBlock
            code={command}
            copyText={command}
            disabled={!copyEnabled}
            disabledTitle={disabledTitle}
          />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-300">Step 2 — Reload Claude Code</p>
          <p className="text-xs text-gray-400">
            Restart Claude Code or run{' '}
            <code className="bg-gray-800 px-1 rounded text-gray-300 font-mono">/reload</code>{' '}
            to pick up the new MCP server.
          </p>
        </div>
      </div>
    );
  }

  if (clientId === 'claude-desktop') {
    return (
      <div className="space-y-4">
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-300">Step 1 — Open the connector settings</p>
          <p className="text-xs text-gray-400">
            Navigate to{' '}
            <span className="text-gray-300">Settings &rarr; Connectors &rarr; Add custom connector</span>.
          </p>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-300">Step 2 — Paste the MCP URL</p>
          <CodeBlock
            code={mcpUrl}
            copyText={mcpUrl}
            disabled={!copyEnabled}
            disabledTitle={disabledTitle}
          />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-300">Step 3 — Save</p>
          <p className="text-xs text-gray-400">Leave OAuth fields empty and save the connector.</p>
        </div>
      </div>
    );
  }

  if (clientId === 'codex') {
    const toml = `[mcp_servers.sharpelab]\nurl = "${mcpUrl}"`;
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-300">Step 1 — Edit your Codex config</p>
          <p className="text-xs text-gray-400">
            Open{' '}
            <code className="bg-gray-800 px-1 rounded text-gray-300 font-mono">~/.codex/config.toml</code>{' '}
            and add:
          </p>
          <CodeBlock
            code={toml}
            copyText={toml}
            disabled={!copyEnabled}
            disabledTitle={disabledTitle}
          />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-300">Step 2 — Restart Codex</p>
          <p className="text-xs text-gray-400">Restart Codex to apply the new MCP server configuration.</p>
        </div>
      </div>
    );
  }

  // Cursor
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-gray-300">Step 1 — Open Cursor MCP settings</p>
        <p className="text-xs text-gray-400">
          Navigate to{' '}
          <span className="text-gray-300">Settings &rarr; MCP &rarr; Add new MCP server</span>.
        </p>
      </div>
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-300">Step 2 — Paste the URL</p>
        <CodeBlock
          code={mcpUrl}
          copyText={mcpUrl}
          disabled={!copyEnabled}
          disabledTitle={disabledTitle}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Secret banner — shown once after generation
// ---------------------------------------------------------------------------

interface SecretBannerProps {
  token: ApiTokenCreated;
}

function SecretBanner({ token }: SecretBannerProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    safeCopy(token.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="rounded-lg border border-green-700 bg-green-900/20 p-4 space-y-3 mb-4">
      <div>
        <p className="text-sm font-semibold text-green-300">
          Token &ldquo;{token.name}&rdquo; generated
        </p>
        <p className="text-xs text-amber-400 mt-0.5">
          Shown once — copy and store it now.
        </p>
      </div>
      <div className="flex items-stretch border border-gray-700 rounded-lg overflow-hidden bg-gray-900/70">
        <code className="flex-1 px-3 py-2.5 text-xs text-green-300 font-mono break-all select-all">
          {token.token}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          aria-label="Copy token"
          className={`shrink-0 px-4 flex items-center gap-1.5 text-xs font-bold border-l border-gray-700 transition-colors ${
            copied
              ? 'bg-primary-900/40 text-primary-400'
              : 'bg-primary-600 hover:bg-primary-500 text-white'
          }`}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main modal component
// ---------------------------------------------------------------------------

export interface ConnectionGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ConnectionGuideModal({ isOpen, onClose }: ConnectionGuideModalProps) {
  const [activeClientId, setActiveClientId] = useState<ClientId>('claude-code');
  const [tokenName, setTokenName] = useState('Claude Code MCP');
  const [generatedToken, setGeneratedToken] = useState<ApiTokenCreated | null>(null);

  const createMutation = useCreateApiToken();

  // Reset state when modal closes.
  // We only want to run this when `isOpen` changes; createMutation.reset is stable.
  // Intentionally omitting createMutation from deps to avoid infinite loops.
  const resetMutation = createMutation.reset;
  useEffect(() => {
    if (!isOpen) {
      setActiveClientId('claude-code');
      setTokenName('Claude Code MCP');
      setGeneratedToken(null);
      resetMutation();
    }
  }, [isOpen, resetMutation]);

  function handleClientSwitch(client: ClientConfig) {
    setActiveClientId(client.id);
    setTokenName(client.tokenName);
  }

  async function handleGenerate() {
    const name = tokenName.trim();
    if (!name || createMutation.isPending) return;
    try {
      const created = await createMutation.mutateAsync({ name });
      setGeneratedToken(created);
    } catch {
      // Error displayed via createMutation.isError
    }
  }

  const activeToken = generatedToken?.token ?? '<your-token>';
  const copyEnabled = generatedToken !== null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Connect your agent" size="lg">
      <div className="space-y-5 p-1">
        {/* Secret banner — shown once after generation */}
        {generatedToken && <SecretBanner token={generatedToken} />}

        {/* Client tab cards */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Select your client
          </p>
          <div className="grid grid-cols-4 gap-2">
            {CLIENTS.map((client) => {
              const isActive = activeClientId === client.id;
              return (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => handleClientSwitch(client)}
                  aria-label={client.label}
                  className={`flex flex-col items-center gap-2 py-3 px-2 rounded-xl border transition-colors ${
                    isActive
                      ? 'border-primary-600 bg-primary-600/10 shadow-[0_0_0_1px_rgba(18,189,161,0.25)]'
                      : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
                  }`}
                >
                  <span
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isActive
                        ? 'bg-primary-600/20 text-primary-400'
                        : 'bg-gray-700/60 text-gray-400'
                    }`}
                  >
                    {client.icon}
                  </span>
                  <span
                    className={`text-xs font-semibold text-center leading-tight ${
                      isActive ? 'text-white' : 'text-gray-400'
                    }`}
                  >
                    {client.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Generate row */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Generate token
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              aria-label="Token name"
              value={tokenName}
              onChange={(e) => setTokenName(e.target.value)}
              placeholder="e.g. Claude Code MCP"
              className="flex-1 bg-gray-800/60 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!tokenName.trim() || createMutation.isPending}
              aria-label="Generate token"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white bg-gradient-to-r from-primary-600 to-primary-500 hover:brightness-110 disabled:from-gray-700 disabled:to-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed transition-all whitespace-nowrap"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 2L3 14h7l-1 8 10-12h-7z" />
              </svg>
              {createMutation.isPending ? 'Generating...' : 'Generate token'}
            </button>
          </div>

          {createMutation.isError && (
            <p className="text-xs text-red-400">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : 'Failed to generate token'}
            </p>
          )}

          {/* Amber nudge — sits next to the generate block until a token exists */}
          {!copyEnabled && (
            <p className="flex items-center gap-1.5 text-xs text-amber-400">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 9v4m0 4h.01M10.3 4.3L2.6 18a2 2 0 001.7 3h15.4a2 2 0 001.7-3L13.7 4.3a2 2 0 00-3.4 0z" />
              </svg>
              Generate a token to get a working URL.
            </p>
          )}
        </div>

        {/* Per-client steps */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Setup steps
          </p>
          <div className="rounded-xl border border-gray-700 bg-gray-900/40 p-4">
            <StepContent
              clientId={activeClientId}
              token={activeToken}
              copyEnabled={copyEnabled}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
